import type { Logger, Unsubscribe } from '../core/services';

/**
 * Makes a stream problem noticeable when the studio tab is not the one being
 * looked at: the tab title is prefixed, and a system notification is shown if
 * the operator has allowed notifications. In-app toasts are useless in a
 * hidden tab, which is exactly when a stream is most likely to break.
 * `notifications: false` (embedded in the portal, where browsers refuse permission to cross-origin
 * frames) keeps the title prefix and reports system notifications as unsupported.
 */
export function createOperatorAlert(
  logger: Logger,
  options: { notifications?: boolean } = {},
): {
  raise(key: string, message: string): void;
  clear(key: string): void;
  requestPermission(): Promise<NotificationPermission | 'unsupported'>;
  dispose: Unsubscribe;
} {
  const active = new Map<string, string>();
  const shown = new Map<string, Notification>();
  const baseTitle = document.title;
  const PREFIX = '\u26A0 ';
  const supported = (): boolean => (options.notifications ?? true) && typeof window !== 'undefined' && 'Notification' in window;

  const syncTitle = () => {
    document.title = active.size > 0 ? `${PREFIX}${baseTitle}` : baseTitle;
  };

  const show = (key: string, message: string) => {
    if (!supported() || Notification.permission !== 'granted') return;
    // Only interrupt the operator when they cannot see the in-app warning.
    if (document.visibilityState === 'visible' && document.hasFocus()) return;
    try {
      shown.get(key)?.close();
      const notification = new Notification('Stream problem', { body: message, tag: `ohmyobs-${key}`, requireInteraction: true });
      notification.onclick = () => window.focus();
      shown.set(key, notification);
    } catch (error) {
      logger.log('debug', 'studio', 'System notification failed', error);
    }
  };

  return {
    raise(key, message) {
      active.set(key, message);
      syncTitle();
      show(key, message);
    },
    clear(key) {
      if (!active.delete(key)) return;
      shown.get(key)?.close();
      shown.delete(key);
      syncTitle();
    },
    async requestPermission() {
      if (!supported()) return 'unsupported';
      try {
        if (Notification.permission !== 'default') return Notification.permission;
        return await Notification.requestPermission();
      } catch (error) {
        logger.log('debug', 'studio', 'Notification permission request failed', error);
        return 'unsupported';
      }
    },
    dispose() {
      for (const notification of shown.values()) notification.close();
      shown.clear();
      active.clear();
      document.title = baseTitle;
    },
  };
}
